import type { Einwilligungen } from './einwilligungen';
export interface CreateEnrollmentPayload {
  unterrichtsformId?: number;
  weekday?: number; // 1..7
  startTime?: string; // HH:MM:SS
  intervalWeeks?: number; // 1|2
  startDate?: string; // YYYY-MM-DD
  durationMin?: number;
  sendToStudent?: boolean;
  subprofileId?: string;
  teacherId?: number;
  debugFees?: boolean;
  // Schritt 2: Paketauswahl
  contingentPackageId?: string;
  followupPackageId?: string;
  packageUnits?: number;
  unitPriceCents?: number;
  discountPercent?: number;
  currency?: string;
  paymentProvider?: string;
  paymentType?: 'immediate' | 'installment';
  installments?: number;
  studentSelectsPackage?: boolean;
  startpaketSelected?: boolean;
  // Beitrag (ADR-0003): gewählte Zahlweise und angezeigter Tarif
  billingIntervalMonths?: number;
  billingTariffId?: number;
  /** Einmalcode für diese Anmeldung; steht als {{gutscheinCode}} im Angebot. */
  couponCode?: string;
  /** Sofort oder Raten je Paket — Vorbelegung für die Anmeldung des Kunden (T-0011). */
  zahlweiseJePaket?: Record<string, 'immediate' | 'installment'>;
  /** Im Gespräch gewähltes Paket (T-0230). */
  vorgewaehltesPaket?: string | null;
  /** Aufnahmegebühr im Assistenten angehakt (T-0054). */
  signupFeeSelected?: boolean;
  // Schritt 3: E-Mail
  emailContent?: string;
  emailSubject?: string;
  courseId?: number;
}

export interface EnrollmentDTO {
  id: number;
  trialLessonId: number;
  subprofileId: string;
  teacherId: number;
  unterrichtsformId?: number | null;
  weekday: number;
  startTime: string;
  intervalWeeks: number;
  startDate: string;
  durationMin: number;
  status: 'draft'|'sent'|'accepted_by_student'|'rejected_by_student'|'converted'|'cancelled';
  courseId?: number | null;
  contingentPackageId?: string | null;
  /** Das vom Kunden gebuchte Startpaket (nach der Bestätigung auf der Website). */
  packageId?: string | null;
  followupPackageId?: string | null;
  packageUnits?: number | null;
  unitPriceCents?: number | null;
  discountPercent?: number | null;
  currency?: string | null;
  paymentProvider?: string | null;
  stripePaymentMethodId?: string | null;
  startpaketSelected?: boolean;
  startpaketUnits?: number | null;
  startpaketPriceCents?: number | null;
  startpaketSignupFeeCents?: number | null;
  paymentStatus?: string | null;
  paymentChargeState?: string | null;
  paymentChargeDueAt?: string | null;
  paymentChargeError?: string | null;
  /** Gewähltes Paket bzw. Beitragstarif — „Anmeldung anpassen" belegt damit vor. */
  billingTariffId?: number | null;
  billingIntervalMonths?: number | null;
  emailSubject?: string | null;
  emailContent?: string | null;
  couponCode?: string | null;
  zahlweiseJePaket?: Record<string, 'immediate' | 'installment'>;
  /** Im Gespräch gewähltes Paket (T-0230). */
  vorgewaehltesPaket?: string | null;
  /** Nur reserviert (kein übernommener Kurs) — beim Anpassen neu zu reservieren. */
  courseReserved?: boolean;
  /** Einwilligungen aus der Anmeldung auf der Website (T-0201). */
  einwilligungen?: Einwilligungen | null;
}

export interface EnrollmentPaymentReadiness {
  ok: boolean;
  enrollmentId: number;
  dueDate?: string | null;
  provider?: string | null;
  canChargeNow: boolean;
  status: 'ready' | 'invalid';
  method: {
    id?: string | null;
    type?: string | null;
    label?: string | null;
    brand?: string | null;
    last4?: string | null;
    expMonth?: number | null;
    expYear?: number | null;
    bankCode?: string | null;
    country?: string | null;
  };
  checks?: {
    stripeChecked?: boolean;
    cardExpired?: boolean;
    customerId?: string | null;
    paymentMethodCustomerId?: string | null;
  };
  simulation?: {
    performed?: boolean;
    ok?: boolean | null;
    message?: string | null;
    intentStatus?: string | null;
  };
  errors?: string[];
}
export type EnrollmentStatus = EnrollmentDTO['status'];

import { httpClient } from './api';

export async function createEnrollment(trialId: string|number, payload: CreateEnrollmentPayload): Promise<EnrollmentDTO> {
  const res = await httpClient<EnrollmentDTO>(`/api/trials/${trialId}/enrollments`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.data) throw new Error(res.error || 'Anmeldung konnte nicht erstellt werden');
  return res.data;
}

export async function sendEnrollmentOffer(id: number): Promise<EnrollmentDTO> {
  const res = await httpClient<EnrollmentDTO>(`/api/enrollments/${id}/send-offer`, { method: 'POST' });
  if (!res.data) throw new Error(res.error || 'Anmeldung konnte nicht versendet werden');
  return res.data;
}

export async function getEnrollmentForTrial(trialId: string|number): Promise<EnrollmentDTO | null> {
  const res = await httpClient<EnrollmentDTO | null>(`/api/trials/${trialId}/enrollments`);
  if (res.error) throw new Error(res.error || 'Anmeldung nicht abrufbar');
  return (res.data ?? null) as (EnrollmentDTO | null);
}

export async function getEnrollmentPaymentReadiness(id: number): Promise<EnrollmentPaymentReadiness> {
  const res = await httpClient<EnrollmentPaymentReadiness>(`/api/payments/enrollments/${id}/readiness`);
  if (!res.data) throw new Error(res.error || 'Zahlungsprüfung konnte nicht geladen werden');
  return res.data;
}

export async function getEnrollmentDownloadUrl(id: number): Promise<string> {
  const res = await httpClient<{ url: string }>(`/api/enrollments/${id}/download-url`);
  if (!res.data) throw new Error(res.error || 'Download-Link für Anmeldung nicht verfügbar');
  return res.data.url as string;
}

export interface StartCoursePayload {
  emailContent?: string;
  emailSubject?: string;
  sendEmail?: boolean;
}

export interface StartCourseResult {
  courseId: number;
  trialId: number;
  enrollmentId: number;
  course?: Record<string, unknown>;
  validation?: CourseValidationResult;
}

export interface CourseValidationResult {
  isValid: boolean;
  currentCount: number;
  maxCount: number;
  minCount?: number;
  warning?: string;
  alternativeForms?: Array<{ id: number; name: string; min_schueler?: number; max_schueler?: number }>;
  existingParticipants?: Array<Record<string, unknown>>;
}

export async function startCourse(enrollmentId: number, payload: StartCoursePayload): Promise<StartCourseResult> {
  const res = await httpClient<StartCourseResult>(`/api/enrollments/${enrollmentId}/start-course`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.data) throw new Error(res.error || 'Kurs konnte nicht gestartet werden');
  return res.data;
}

export async function validateCourseCapacity(courseId: number, startDate: string): Promise<CourseValidationResult> {
  const res = await httpClient<CourseValidationResult>(`/api/courses/${courseId}/validation?startDate=${startDate}`);
  if (!res.data) throw new Error(res.error || 'Validierung fehlgeschlagen');
  return res.data;
}

export async function chargeEnrollmentNow(id: number): Promise<{
  ok: boolean;
  enrollmentId: number;
  paymentIntentId?: string;
  status?: string;
  error?: string;
}> {
  const res = await httpClient<{
    ok: boolean;
    enrollmentId: number;
    paymentIntentId?: string;
    status?: string;
    error?: string;
  }>(`/api/payments/enrollments/${id}/charge-now`, {
    method: 'POST',
  });
  if (!res.data) throw new Error(res.error || 'Manuelle Belastung konnte nicht ausgelöst werden');
  if (!res.data.ok) throw new Error(res.data.error || 'Manuelle Belastung fehlgeschlagen');
  return res.data;
}

export async function runScheduledEnrollmentCharges(limit = 100): Promise<{
  ok: boolean;
  total: number;
  processed: number;
  failed: number;
  results: Array<{
    ok: boolean;
    enrollmentId: number;
    paymentIntentId?: string;
    status?: string;
    error?: string;
  }>;
}> {
  const res = await httpClient<{
    ok: boolean;
    total: number;
    processed: number;
    failed: number;
    results: Array<{
      ok: boolean;
      enrollmentId: number;
      paymentIntentId?: string;
      status?: string;
      error?: string;
    }>;
  }>('/api/jobs/payments/charge-course-starts/run', {
    method: 'POST',
    body: JSON.stringify({ limit }),
  });

  if (!res.data) throw new Error(res.error || 'Cron-Run konnte nicht gestartet werden');
  if (!res.data.ok) throw new Error('Cron-Run fehlgeschlagen');
  return res.data;
}

type PaketMitEinheiten = { id: string | number; units?: number | null; special?: boolean | number | null };

/**
 * Das Paket, mit dem der Unterricht beginnt — dessen Termine nennt der Auftrag an
 * die Lehrkraft (T-0284).
 *
 * Maßgeblich ist, was der Kunde gebucht hat: zuerst sein Startpaket, sonst sein
 * Folgepaket. Vor der Bestätigung gelten die Vorgaben aus dem Anmelde-Assistenten.
 * Bisher zählte allein der Schalter „Startpaket hinzufügen“ — war er aus und der
 * Kunde buchte trotzdem ein Startpaket, nannte das Angebot 3 Termine und die
 * Bestätigung 9.
 */
export function erstesPaket<T extends PaketMitEinheiten>(
  enrollment: Pick<EnrollmentDTO, 'packageId' | 'followupPackageId' | 'startpaketSelected' | 'contingentPackageId' | 'vorgewaehltesPaket'> | null | undefined,
  pakete: readonly T[],
): T | null {
  if (!enrollment) return null;
  const finde = (id: string | null | undefined) =>
    id ? pakete.find((paket) => String(paket.id) === String(id)) ?? null : null;
  return (
    finde(enrollment.packageId)
    ?? finde(enrollment.followupPackageId)
    ?? (enrollment.startpaketSelected
      ? finde(enrollment.contingentPackageId) ?? pakete.find((paket) => Boolean(paket.special)) ?? null
      : null)
    ?? finde(enrollment.vorgewaehltesPaket)
  );
}

